import clsx from "clsx"
import moment from "moment"
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react"
import { useDispatch, useSelector } from "react-redux"

import { ACTIONS } from "src/constants"
import { RECHECK_INTERVAL, RETRY_COUNT } from "src/constants/common"
import { useMutation } from "src/hooks/APIHooks"
import { selectBuildingSummary } from "src/redux/slicers/buildingSummarySlice"
import {
  getFilter,
  getIsValid,
  getReportStatus,
  getSelectedAHUProperties,
  getSelectedEquipments,
  setIsValid,
  setReportStatus
} from "src/redux/slicers/reportSlice"
import { downloadFileUsingS3 } from "src/utils/CommonMethods"
import { resignReportUrl } from "src/utils/reportUrlUtils"

import styles from "../ChillerReport/ChillerPerformanceReport.module.scss"
import { REPORT_STATE } from "../constants"
import { generateEquipmentPerformanceReport } from "../graphql"
import AHUEquipmentList from "./AHUEquipmentList"
import AHUProperties from "./AHUProperties"

const AHUReport = forwardRef((props: any, ref: any) => {
  const { selectedLanguage, selectedMetric } = props
  useImperativeHandle(ref, () => ({
    onClickGenerate: generateReportHandler
  }))

  const isExportingToCloudOnly = useRef(false)

  const dispatch = useDispatch()
  const { data: buildingSummary } = useSelector(selectBuildingSummary)
  const selectedEquipments = useSelector(getSelectedEquipments)
  const selectedProperties = useSelector(getSelectedAHUProperties)
  const { cancelled, error, loading } = useSelector(getReportStatus)
  const [reportInfo, setReportInfo] = useState(REPORT_STATE)
  const { startDate, endDate } = useSelector(getFilter)
  const isValid = useSelector(getIsValid)
  useEffect(() => {
    dispatch(
      setIsValid(
        Object?.keys(selectedEquipments)?.length > 0 &&
          selectedProperties?.length > 0
      )
    )
  }, [selectedEquipments, selectedProperties, dispatch])

  const checkReportIsFound = useCallback(async () => {
    try {
      const response = await fetch(reportInfo.url)
      if (response?.status === 404) {
        setReportInfo({
          ...reportInfo,
          retryCount: reportInfo.retryCount + 1
        })
        if (reportInfo.retryCount + 1 >= RETRY_COUNT) {
          dispatch(setReportStatus(ACTIONS.ERROR))
        }
      } else if (response?.status === 200) {
        dispatch(setReportStatus(ACTIONS.COMPLETE))
        if (!isExportingToCloudOnly.current) {
          const fileName =
            buildingSummary?.name +
            "_" +
            moment(new Date(startDate)).format("MM-DD-YYYY") +
            "_" +
            selectedEquipments?.name +
            "_AHU_Performance"

          downloadFileUsingS3(reportInfo.url, fileName)
        }
      } else if (response?.status === 403) {
        // S3 pre-signed URL has expired (InvalidToken). Re-sign and retry.
        const freshUrl = await resignReportUrl(reportInfo.url, buildingSummary?.id || "")
        if (freshUrl) {
          setReportInfo({ ...reportInfo, url: freshUrl })
        } else {
          dispatch(setReportStatus(ACTIONS.ERROR))
        }
      } else {
        dispatch(setReportStatus(ACTIONS.ERROR))
      }
    } catch {
      dispatch(setReportStatus(ACTIONS.ERROR))
    }
  }, [reportInfo, dispatch, buildingSummary, startDate, selectedEquipments])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (cancelled || !loading || !reportInfo.url || error) {
      clearTimeout(timeout)
    } else if (reportInfo.retryCount === 0) {
      timeout = setTimeout(() => {
        checkReportIsFound()
      }, 0)
    } else {
      timeout = setTimeout(() => {
        checkReportIsFound()
      }, RECHECK_INTERVAL)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [cancelled, loading, reportInfo, checkReportIsFound, error])

  const { onSubmit: generateReportQuery } = useMutation({
    query: generateEquipmentPerformanceReport,
    onSuccess: (data: any) => {
      if (data?.data?.generateEquipmentPerformanceReport) {
        const { body } = JSON.parse(
          data?.data?.generateEquipmentPerformanceReport
        )

        setReportInfo({
          url: body,
          retryCount: 0
        })
      }
    },
    onError: () => {
      dispatch(setReportStatus(ACTIONS.ERROR))
    }
  })

  // **** Below method is invoked by ref **** /
  const generateReportHandler = (saveTo: string) => {
    if (isValid) {
      dispatch(setReportStatus(ACTIONS.LOADING))
      const formattedStartDate = moment(new Date(startDate))
        .locale("en")
        .format("YYYY-MM-DD")
      const formattedEndDate = moment(new Date(endDate))
        .locale("en")
        .format("YYYY-MM-DD")
      const currentDate = moment(new Date(buildingSummary?.localTime))
        .locale("en")
        .format("YYYY-MM-DD")

      isExportingToCloudOnly.current = saveTo === "cloud"

      generateReportQuery({
        requestBody: JSON.stringify({
          sections: selectedProperties,
          bId: buildingSummary?.id,
          bName: buildingSummary?.name,
          location: buildingSummary?.address,
          postalCode: buildingSummary?.postalCode,
          city: buildingSummary?.city,
          state: buildingSummary?.state,
          eId: selectedEquipments?.id,
          eName: selectedEquipments?.name,
          eType: selectedEquipments?.type,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
          current_date: currentDate,
          systemOfMeasure: selectedMetric?.metricCode,
          language: selectedLanguage?.langCode,
          report: "ahupr",
          save: saveTo === "cloud" || saveTo === "all" ? "cloud" : "local"
        })
      })
    }
  }

  return (
    <div className={clsx(styles.chillerReportWrapper)}>
      <AHUEquipmentList />
      <AHUProperties />
    </div>
  )
})

AHUReport.displayName = "AHUReport"
export default AHUReport
